/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import { FaPrint } from 'react-icons/fa';

import api from '../../../../services/api';

import { formatPrice, TIPO_USUARIO } from '../../../../utils/funcoes';

import { useToast } from '../../../../hooks/toast';
import { useClinica } from '../../../../hooks/clinica';
import { useAuth } from '../../../../hooks/auth';

import Input from '../../../../components/Input';
import Select from '../../../../components/Select';
import SelectSync, { IOption } from '../../../../components/SelectSync';
import Loading from '../../../../components/Loading';
import Paginacao from '../../../../components/Paginacao';

import getValidationErrors from '../../../../utils/getValidationErrors';

import { RelatorioHeader } from './styles';
import getResponseErrors from '../../../../utils/getResponseErrors';
import { useMedico } from '../../../../hooks/medico';
import HeaderSimples from '../../components/HeaderSimples';

interface IPagamento {
  CprCodigo: number;
}

interface IMedico {
  UsuCodigo: number;
  UsuNome: string;
}
export interface IPagamentosMedicosBase {
  id: number;
  UsuMedCodigo: number;
  agendamentos: string;
  valorApurado: number;
  acrescimoDesconto: number;
  motivo: string;
  recibo: string;
  created_at: Date;
}
interface IPagamentosMedicos extends IPagamentosMedicosBase {
  pagamento: IPagamento;
  dataFormatada: string;
  valorPagoFormatado: string;
  medico: IMedico;
}

interface IRetornoRelatorio {
  data: IPagamentosMedicos[];
  count: number;
}

interface ITotais {
  totalPagoFormatado: string;
  totalPagarFormatado: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
  UsuMedCodigo: string;
  CprHistorico: string;
  CprFormaPag: string;
}

interface IPaginacao extends IFormBusca {
  limite: number;
  pagina: number;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const DespesasPagamentoMedico: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica } = useClinica();

  const { optionsMedico, buscaOptionsMedico } = useMedico();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IPagamentosMedicos[]>();
  const [totais, setTotais] = useState<ITotais>();
  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [totalRegistros, setTotalRegistros] = useState<number>();
  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();
  const [clinica, setClinica] = useState('');

  const exibeTotais = user.UsuTipo === TIPO_USUARIO.ADMINISTRADOR;

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  useEffect(() => {
    async function loadMedicos(): Promise<void> {
      await buscaOptionsMedico();
    }
    if (!optionsMedico || Object.keys(optionsMedico).length === 0)
      loadMedicos();
  }, [buscaOptionsMedico, optionsMedico]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;
        const {
          dataFim,
          dataInicio,
          CliCodigo,
          UsuMedCodigo,
          limite,
          pagina,
        } = paginacao;

        const dataInicioSelecionada = parseISO(dataInicio.toString());
        const dataFimSelecionada = parseISO(dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')} `,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const buscaMedico =
          UsuMedCodigo !== '' ? `&UsuMedCodigo=${UsuMedCodigo}` : '';

        const response = await api.get<IRetornoRelatorio>(
          `/pagamentosmedicos?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&CliCodigo=${CliCodigo}${buscaMedico}`,
        );

        const { data, count } = response.data;

        const totalPago = 0;
        const totalPagar = 0;

        const dadosFormatados = data.map(rel => {
          const dataFormatada = format(
            parseISO(rel.created_at.toString()),
            'dd/MM/yyyy',
          );

          return {
            ...rel,
            dataFormatada,
            valorPagoFormatado: formatPrice(
              rel.valorApurado + rel.acrescimoDesconto,
            ),
          };
        });

        setDadosRelatorio(dadosFormatados);
        setTotalRegistros(count);
        setTotais({
          totalPagoFormatado: formatPrice(totalPago),
          totalPagarFormatado: formatPrice(totalPagar),
        });
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [paginacao, addToast]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().required(),
          UsuMedCodigo: Yup.string().nullable(true),
          CprFormaPag: Yup.string().nullable(true),
          CprHistorico: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setClinica(
          optionsClinica.find(cli => cli.value === dados.CliCodigo)?.label ||
            '',
        );

        setPaginacao({
          ...dados,
          pagina: 1,
          limite: 300,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, optionsClinica],
  );

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}

      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <Form
            ref={formRefBusca}
            initialData={initialData}
            onSubmit={handleSubmitBusca}
          >
            <Row noGutters>
              <Col md={2} className="pr-1">
                <Input name="dataInicio" label="De" type="date" required />
              </Col>
              <Col md={2} className="pr-1">
                <Input name="dataFim" label="Até" type="date" required />
              </Col>

              <Col md={3} className="pr-1">
                <SelectSync
                  name="UsuMedCodigo"
                  label="Médico"
                  options={optionsMedico}
                  isClearable
                  handleSelectChange={handleOnChangeVazio}
                />
              </Col>
              <Col md={3} className="pr-1">
                <Select
                  name="CliCodigo"
                  label="Clinica"
                  options={optionsClinica}
                  disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                />
              </Col>

              <Col md={1} className="mt-1">
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        <div className="bg-white border p-2 my-3">
          <HeaderSimples dataImpressao={dataExibe?.dataHora} />
        </div>

        <RelatorioHeader>
          DESPESAS {`- ${clinica} -`} {dataExibe?.dataDiaSemana}
        </RelatorioHeader>
        <div className="bg-white border p-2" style={{ minHeight: '300px' }}>
          {dadosRelatorio && (
            <>
              <b>Total de {totalRegistros} registros</b>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '85px' }}>Data</th>
                    <th style={{ minWidth: '160px' }}>Médico</th>
                    <th style={{ minWidth: '160px' }}>Observação</th>
                    <th style={{ width: '85px' }}>Qtd</th>
                    <th style={{ width: '120px' }}>Valor</th>
                    <th>-</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <tr
                        key={`despesas-${rel.id}`}
                        data-id={rel.id}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>{rel.dataFormatada}</td>
                        <td>{rel.medico.UsuNome}</td>
                        <td>{rel.motivo}</td>
                        <td>{rel.agendamentos.split(',').length}</td>
                        <td>{rel.valorPagoFormatado}</td>

                        <td>
                          <Link
                            data-id={rel.id}
                            type="button"
                            style={{ padding: '1px 8px' }}
                            // onClick={
                            //   () => handleShowRecibo(rel.recibo)
                            //   // eslint-disable-next-line react/jsx-curly-newline
                            // }
                            to={`/impressaopagamentoMedico/${rel.id}`}
                            target="_blank"
                          >
                            <FaPrint size={14} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  {exibeTotais && (
                    <tr
                      key="fdfdsfsdfdf"
                      style={{
                        fontSize: 'Small',
                        height: '30px',
                        fontWeight: 'bold',
                      }}
                    >
                      <td> </td>
                      <td colSpan={2} style={{ textAlign: 'right' }}>
                        Total Pago: {totais?.totalPagoFormatado}
                      </td>
                      <td colSpan={3} style={{ textAlign: 'right' }}>
                        Total a Pagar: {totais?.totalPagarFormatado}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
          <Paginacao
            limitePagina={paginacao?.limite || 300}
            paginasVizinhas={2}
            totalRegistros={totalRegistros}
            paginaInicio={paginacao?.pagina}
            handleProximaPagina={handleProximaPagina}
          />
        </div>
      </>
    </Container>
  );
};

export default DespesasPagamentoMedico;
